import Swal from 'sweetalert2';

// Wspólne powiadomienia i komunikaty błędów dla całej aplikacji.
// - notifySuccess / notifyError / notifyInfo: krótkie powiadomienie w rogu (zamiast alert())
// - confirmAction: okno potwierdzenia przed nieodwracalną akcją
// - getApiErrorMessage: czytelny polski komunikat z błędu axios

const toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 4000,
    timerProgressBar: true,
});

export const notifySuccess = (text) => toast.fire({ icon: 'success', title: text });

export const notifyError = (text) => toast.fire({ icon: 'error', title: text, timer: 6000 });

export const notifyInfo = (text) => toast.fire({ icon: 'info', title: text });

export const confirmAction = async ({
    title,
    text,
    confirmButtonText = 'Tak, usuń',
    cancelButtonText = 'Anuluj',
}) => {
    const result = await Swal.fire({
        title,
        text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        background: '#fff',
        color: '#000',
    });
    return result.isConfirmed;
};

/**
 * Okno z jednym polem tekstowym (np. nazwa wzoru). Zwraca wpisany tekst albo null po anulowaniu.
 */
export const promptText = async ({
    title,
    value = '',
    placeholder = '',
    confirmButtonText = 'Zapisz',
    cancelButtonText = 'Anuluj',
    maxLength = 80,
}) => {
    const result = await Swal.fire({
        title,
        input: 'text',
        inputValue: value,
        inputPlaceholder: placeholder,
        inputAttributes: { maxlength: String(maxLength) },
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
        confirmButtonColor: '#1c3f58',
        background: '#fff',
        color: '#000',
        inputValidator: text => (!text || !text.trim() ? 'Wpisz nazwę.' : undefined),
    });
    return result.isConfirmed ? result.value.trim() : null;
};

// Komunikaty backendu, które mogą trafić do użytkownika, a są po angielsku
const BACKEND_MESSAGES = {
    'Invalid login or password': 'Niepoprawny adres e-mail lub hasło.',
    'User with this email already exists': 'Konto z tym adresem e-mail już istnieje.',
    'Owner password is incorrect': 'Twoje hasło jest nieprawidłowe.',
    'Old password is incorrect': 'Obecne hasło jest nieprawidłowe.',
    'Employee not found': 'Nie znaleziono pracownika.',
    'User not found': 'Nie znaleziono użytkownika.',
    'Username, email, and password are required': 'Uzupełnij nazwę, e-mail i hasło.',
    'Invalid code': 'Kod jest nieprawidłowy.',
    'Code expired': 'Kod wygasł.',
    'Usage limit reached': 'Limit użyć tego kodu został wyczerpany.',
    'A promo code has already been used for this funeral home': 'Twój zakład wykorzystał już kod promocyjny.',
    'User already used a promo code': 'Twój zakład wykorzystał już kod promocyjny.',
    'Access denied, owner only': 'Tę operację może wykonać tylko właściciel konta.',
    'Template not found': 'Nie znaleziono szablonu.',
    'Pattern limit reached': 'Osiągnięto limit wzorów dla tego rodzaju materiału.',
    'Access denied, patterns permission required': 'Nie masz uprawnień do tworzenia i zmiany wzorów.',
    'Pattern not found': 'Nie znaleziono wzoru.',
};

/**
 * Zamienia błąd axios na polski komunikat dla użytkownika.
 * @param {unknown} error błąd z axios
 * @param {string} fallback komunikat, gdy nic konkretnego nie wiadomo
 * @param {Record<number, string>} byStatus komunikaty dla konkretnych kodów HTTP (mają pierwszeństwo)
 */
export const getApiErrorMessage = (error, fallback = 'Wystąpił błąd. Spróbuj ponownie.', byStatus = {}) => {
    if (!error?.response) {
        return 'Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.';
    }

    const { status, data } = error.response;
    const message = typeof data === 'string' ? data : data?.message;

    if (byStatus[status]) {
        return byStatus[status];
    }
    if (typeof message === 'string' && BACKEND_MESSAGES[message]) {
        return BACKEND_MESSAGES[message];
    }
    // Limit prób – backend zwraca gotowy polski komunikat
    if (status === 429) {
        return typeof message === 'string' ? message : 'Zbyt wiele prób. Spróbuj ponownie później.';
    }
    if (status >= 500) {
        return 'Błąd serwera. Spróbuj ponownie za chwilę.';
    }
    // Pozostałe komunikaty pokazujemy tylko, jeśli wyglądają na polskie (zawierają polskie znaki)
    if (typeof message === 'string' && /[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/.test(message)) {
        return message;
    }
    return fallback;
};
